import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";

const AUTH_BG = "https://images.unsplash.com/photo-1747673002516-f11a48cb0ce2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHRleHR1cmUlMjBlYXJ0aCUyMHRvbmVzfGVufDB8fHx8MTc4MzExMDgzNXww&ixlib=rb-4.1.0&q=85";

export default function RegisterPage() {
  const { user, localMode, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && !localMode) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    setError("");
    const res = await register(name, email, password);
    setLoading(false);
    if (res.ok) {
      const m = res.migration || {};
      if (m.migrated && m.imported) {
        const n = (m.imported.transactions || 0) + (m.imported.recurring || 0) + (m.imported.category_budgets || 0);
        if (n > 0) toast.success(`Welcome! We moved ${n} items from your local ledger into your new account.`);
      }
      navigate("/dashboard");
    } else setError(res.error);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-brand-bg font-body">
      <SEO
        title="Create your free account"
        description="Create a free Tracker Daily Money account and start tracking your cash flow in minutes. No bank connection required. Private, calm, and yours."
        path="/register"
        noindex
      />
      <div className="flex flex-col justify-between p-8 md:p-16">
        <Link to="/" className="flex items-center gap-2 w-fit" data-testid="brand-logo">
          <div className="h-9 w-9 rounded-md bg-brand-primary flex items-center justify-center">
            <Wallet className="h-4 w-4 text-brand-bg" strokeWidth={1.75} />
          </div>
          <span className="font-display font-black text-xl tracking-tight text-brand-charcoal">Tracker Daily</span>
        </Link>

        <div className="max-w-md w-full mx-auto md:mx-0 animate-fade-up">
          <div className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-brand-muted">Get started</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-brand-charcoal mb-3">
            Create your ledger
          </h1>
          <p className="text-brand-muted mb-8 leading-relaxed">
            Start tracking money in and money out. It&apos;s free, private, and quietly effective.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-brand-charcoal">Full name</Label>
              <Input
                id="name" required value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe" data-testid="register-name"
                className="mt-1 border-brand-border bg-brand-surface"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-brand-charcoal">Email</Label>
              <Input
                id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" data-testid="register-email"
                className="mt-1 border-brand-border bg-brand-surface"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-brand-charcoal">Password</Label>
              <Input
                id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters" data-testid="register-password"
                className="mt-1 border-brand-border bg-brand-surface"
              />
            </div>

            {error && (
              <div data-testid="register-error" className="text-sm text-brand-danger bg-brand-danger/10 border border-brand-danger/20 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <Button
              type="submit" disabled={loading} data-testid="register-submit-button"
              className="w-full bg-brand-primary text-brand-bg hover:bg-brand-primaryHover rounded-md py-6 text-base font-semibold"
            >
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-brand-muted">
            Already have an account?{" "}
            <Link to="/login" className="text-brand-primary font-semibold hover:underline" data-testid="link-login">
              Sign in
            </Link>
          </p>
        </div>

        <div className="text-xs text-brand-muted mt-8">© Tracker Daily Money · Your money, your rhythm</div>
      </div>

      <div className="hidden md:block relative overflow-hidden">
        <img src={AUTH_BG} alt="Abstract earth tones" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-primary/40 via-transparent to-transparent" />
      </div>
    </div>
  );
}
